import {useCallback, useEffect, useRef, useState} from 'react';
import type {APIState} from './types';

export class SubmitError<T = unknown> extends Error {
  readonly status: number;
  readonly data: T;

  constructor(status: number, data: T) {
    super(`HTTP error! status: ${status}`);
    // Fixes broken prototype chain when subclassing Error in TS/Babel downcompiled targets
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = 'SubmitError';
    this.status = status;
    this.data = data;
  }
}

/**
 * Custom hook for submitting data to API endpoints
 * @template T - The type of data being returned from the API
 * @param fileName - Name for logging purposes
 * @returns Object containing loading, error, data states and submit function
 */
const useSubmitData = <
  T,
  TBody extends Record<string, unknown> | string =
    | Record<string, unknown>
    | string,
>({
  fileName,
}: {
  fileName: string;
}) => {
  const [submitState, setSubmitState] = useState<APIState<T>>({
    isLoading: false,
    isError: false,
    data: null,
  });
  const abortControllerRef = useRef<AbortController | null>(null);

  const setLoading = useCallback((isLoading: boolean) => {
    setSubmitState((prevState) => ({...prevState, isLoading}));
  }, []);

  const setError = useCallback((isError: boolean) => {
    setSubmitState((prevState) => ({...prevState, isError}));
  }, []);

  const setData = useCallback((data: T | null) => {
    setSubmitState((prevState) => ({...prevState, data: data ?? null}));
  }, []);

  const submit = useCallback(
    async (
      url: string,
      method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
      body?: TBody
    ) => {
      // Cancel previous request if any
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      try {
        setLoading(true);
        setError(false);

        let serializedBody: string | undefined;
        if (body !== undefined) {
          serializedBody =
            typeof body === 'string' ? body : JSON.stringify(body);
        }

        const response = await fetch(url, {
          method,
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'X-Forwarded-Proto': 'https',
            'X-CSRF-Token': window.csrfToken ?? '',
          },
          body: serializedBody,
          signal: abortControllerRef.current.signal,
        });

        let responseBody: unknown = null;
        if (response.status !== 204) {
          const text = await response.text();
          if (text) {
            responseBody = JSON.parse(text) as unknown;
          }
        }

        if (!response.ok) {
          throw new SubmitError(response.status, responseBody);
        }
        setData(responseBody as T);
        return responseBody;
      } catch (error) {
        // Don't log error if request was aborted
        if (error instanceof Error && error.name !== 'AbortError') {
          console.error(
            `${fileName.toUpperCase()}: something went wrong while submitting to ${url} with error: ${
              error.message
            }`
          );
          setError(true);
          setData(null);
        }
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [fileName, setData, setError, setLoading]
  );

  // Cleanup: abort any ongoing requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return {...submitState, submit};
};

export default useSubmitData;
